import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Column } from 'primereact/column';
import { Skeleton } from 'primereact/skeleton';
import { useQuery } from '@tanstack/react-query';
import AdminSearchResultsGrid from 'components/admin/search/AdminSearchResultsGrid';
import { FieldPanelFullWidth } from 'styles/v2/Styles.styled';
import { useCertificationBodySearch } from 'hooks/useVerifierSearchHook';
import useDebounce from 'hooks/useDebounce';
import { getCertificationBodyAccreditedByOptions } from 'services/api/verifiersApi';
import { useCountriesLookup } from 'services/api/queries';
import { CertificationBodyListItem } from 'types/verifiers/VerifierModels';
import EmailCell from '../components/EmailCell';
import TruncatedCell from '../components/TruncatedCell';
import FilterSelect from '../../../components/filters/FilterSelect';
import SearchInput from '../../../components/filters/SearchInput';
import { Option } from 'types/types';
import NoResultsFound from '../components/NoResultsFound';
import { GLOBAL_OPTION_VALUE, PAGE_SIZES, SKELETON_ROWS } from '../constants';
import { Container, SearchPanel, FilterRow, FilterField, SearchFilterField } from './tabStyles';

const CertificationBodiesTab: React.FC = () => {
  const { isLoading, searchResult, onSearch, pagination, onPageChange } = useCertificationBodySearch();

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 2000);
  const [selectedAccreditedBy, setSelectedAccreditedBy] = useState<Option[]>([]);
  const [selectedMarketplace, setSelectedMarketplace] = useState<Option[]>([]);

  const { data: accreditedByList = [] } = useQuery({
    queryKey: ['verifiers', 'certification-bodies', 'accredited-by'],
    queryFn: getCertificationBodyAccreditedByOptions,
    staleTime: 5 * 60 * 1000,
  });

  const { data: countries = [] } = useCountriesLookup();

  const accreditedByOptions = useMemo(() => accreditedByList.map((o) => ({ label: o, value: o })), [accreditedByList]);

  const marketplaceOptions = useMemo(
    () => [
      { label: 'Global', value: GLOBAL_OPTION_VALUE },
      ...countries.map((c) => ({ label: c.name ?? '', value: c.id ?? '' })),
    ],
    [countries]
  );

  const buildRequest = useCallback(() => {
    const selectedValues = selectedMarketplace.map((o) => o.value);
    const countryIds = selectedValues.filter((v) => v !== GLOBAL_OPTION_VALUE);
    return {
      searchTerm: debouncedSearchTerm || undefined,
      accreditedByList: selectedAccreditedBy.length > 0 ? selectedAccreditedBy.map((o) => o.value) : undefined,
      countryIds: countryIds.length > 0 ? countryIds : undefined,
      includeGlobal: selectedValues.includes(GLOBAL_OPTION_VALUE),
    };
  }, [debouncedSearchTerm, selectedAccreditedBy, selectedMarketplace]);

  const skipNextDebounce = useRef(false);

  useEffect(() => {
    if (skipNextDebounce.current) {
      skipNextDebounce.current = false;
      return;
    }
    onSearch(buildRequest());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, selectedAccreditedBy, selectedMarketplace]);

  const handleClearSearch = useCallback(() => {
    skipNextDebounce.current = true;
    setSearchTerm('');
    const selectedValues = selectedMarketplace.map((o) => o.value);
    const countryIds = selectedValues.filter((v) => v !== GLOBAL_OPTION_VALUE);
    onSearch({
      searchTerm: undefined,
      accreditedByList: selectedAccreditedBy.length > 0 ? selectedAccreditedBy.map((o) => o.value) : undefined,
      countryIds: countryIds.length > 0 ? countryIds : undefined,
      includeGlobal: selectedValues.includes(GLOBAL_OPTION_VALUE),
    });
  }, [selectedMarketplace, selectedAccreditedBy, onSearch]);

  const emailBodyTemplate = useCallback((row: CertificationBodyListItem) => <EmailCell email={row.email} />, []);
  const organisationBodyTemplate = useCallback(
    (row: CertificationBodyListItem) => <TruncatedCell value={row.organisation} />,
    []
  );
  const accreditedByBodyTemplate = useCallback(
    (row: CertificationBodyListItem) => <TruncatedCell value={row.accreditedBy} />,
    []
  );
  const marketplaceBodyTemplate = useCallback(
    (row: CertificationBodyListItem) => <TruncatedCell value={row.marketplace} />,
    []
  );

  return (
    <Container>
      <SearchPanel>
        <FilterRow>
          <SearchFilterField>
            <SearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClear={handleClearSearch}
              placeholder="Type organisation name or email here"
            />
          </SearchFilterField>
          <FilterField>
            <FilterSelect
              isMulti={true}
              options={accreditedByOptions}
              value={selectedAccreditedBy}
              onChange={setSelectedAccreditedBy}
              label="Accredited by"
            />
          </FilterField>
          <FilterField>
            <FilterSelect
              isMulti={true}
              options={marketplaceOptions}
              value={selectedMarketplace}
              onChange={setSelectedMarketplace}
              label="Marketplace"
            />
          </FilterField>
        </FilterRow>
      </SearchPanel>

      {isLoading && !searchResult ? (
        <FieldPanelFullWidth>
          <AdminSearchResultsGrid
            value={SKELETON_ROWS}
            totalRecords={0}
            pageIndex={0}
            pageSize={PAGE_SIZES[0]}
            onPageChange={() => {}}
            pageSizeOptions={PAGE_SIZES}
            style={{ width: '100%' }}
            tableStyle={{ minWidth: '40rem', tableLayout: 'fixed' }}
          >
            <Column field="organisation" header="Organisation" style={{ width: 260 }} body={<Skeleton />} />
            <Column field="accreditedBy" header="Accredited by" style={{ width: 275 }} body={<Skeleton />} />
            <Column field="marketplace" header="Marketplace" style={{ width: 228 }} body={<Skeleton />} />
            <Column field="email" header="Contact email" style={{ width: 275 }} body={<Skeleton />} />
          </AdminSearchResultsGrid>
        </FieldPanelFullWidth>
      ) : (
        searchResult &&
        (searchResult.totalCount > 0 ? (
          <FieldPanelFullWidth>
            <AdminSearchResultsGrid
              value={searchResult.items}
              totalRecords={searchResult.totalCount}
              pageIndex={pagination.pageIndex}
              pageSize={pagination.pageSize}
              onPageChange={onPageChange}
              pageSizeOptions={PAGE_SIZES}
              loading={isLoading}
              style={{ width: '100%' }}
              tableStyle={{ minWidth: '40rem', tableLayout: 'fixed' }}
            >
              <Column field="organisation" header="Organisation" style={{ width: 260 }} body={organisationBodyTemplate} />
              <Column field="accreditedBy" header="Accredited by" style={{ width: 275 }} body={accreditedByBodyTemplate} />
              <Column field="marketplace" header="Marketplace" style={{ width: 228 }} body={marketplaceBodyTemplate} />
              <Column field="email" header="Contact email" style={{ width: 275 }} body={emailBodyTemplate} />
            </AdminSearchResultsGrid>
          </FieldPanelFullWidth>
        ) : (
          <NoResultsFound />
        ))
      )}
    </Container>
  );
};

export default CertificationBodiesTab;
